import React from 'react';
import styled from '@emotion/styled';
import { mediaQuery } from '@modernhealth/vital-foundation';
import {
    BodyTextMedium,
    BodyTextLarge,
    Grid,
    GridItem,
    HeadingText30,
    HeadingText60,
    HeadingText20,
} from '@modernhealth/vital-components';
import { useTranslation } from 'react-i18next';

import CirclesImage from '../assets/session.png';
import { ReactComponent as CommentImage } from '../assets/comment.svg';
import Chat from '../assets/chat.png';
import Hand from '../assets/hand.png';
import Headphones from '../assets/headphones.png';
import Suitcase from '../assets/suitcase.png';
import { Page } from '../components/PageLayout';
import { BackButton } from '../components/BackButton';

const CommentText = styled(BodyTextMedium)`
    line-height: 32px;
`;

const ImageWrapper = styled.div({
    paddingRight: 0,
    [mediaQuery.minWidth('xl')]: {
        paddingRight: 130,
    },
});

const messages = {
    cards: [
        {
            image: Hand,
            title: 'pages.about.cards.0.title',
            description: 'pages.about.cards.0.description',
        },
        {
            image: Headphones,
            title: 'pages.about.cards.1.title',
            description: 'pages.about.cards.1.description',
        },
        {
            image: Suitcase,
            title: 'pages.about.cards.2.title',
            description: 'pages.about.cards.2.description',
        },
        {
            image: Chat,
            title: 'pages.about.cards.3.title',
            description: 'pages.about.cards.3.description',
        },
    ],
};

export default messages;

export const CirclesAboutPage: React.FC<unknown> = () => {
    const { t } = useTranslation('circles');

    return (
        <Page.Container>
            <Page.Header>
                <BackButton fallbackPath="/circles" />
                <div css={{ marginBottom: 48 }}>
                    <HeadingText60>{t('pages.about.header.title')}</HeadingText60>
                    <BodyTextLarge fontColor="gray65" marginTop={8}>
                        {t('pages.about.header.description')}
                    </BodyTextLarge>
                </div>
            </Page.Header>
            <Page.Content>
                <Grid>
                    <GridItem lg={3} md={2}>
                        <ImageWrapper>
                            <img css={{ maxWidth: '100%' }} alt="" src={CirclesImage} />
                        </ImageWrapper>
                    </GridItem>
                    <GridItem lg={2} md={3} column>
                        {messages.cards.map((card, i) => {
                            return (
                                <Card
                                    key={i}
                                    image={card.image}
                                    title={t(card.title)}
                                    description={t(card.description)}
                                />
                            );
                        })}
                    </GridItem>
                </Grid>
                <HeadingText60 marginTop={48} marginBottom={48}>
                    {t('pages.about.quote.header')}
                </HeadingText60>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                    }}
                >
                    <Quote>
                        {t('pages.about.quotes.0.quote_woman')}
                        {t('pages.about.quote.attribution_woman')}
                    </Quote>

                    <Quote>
                        {t('pages.about.quotes.1.quote_man')}
                        {t('pages.about.quote.attribution_man')}
                    </Quote>
                </div>
            </Page.Content>
        </Page.Container>
    );
};

const CardWrapper = styled.div({
    marginBottom: 48,
    display: 'flex',
    flexDirection: 'row',
    [mediaQuery.maxWidth('xl')]: {
        paddingLeft: 30,
    },
});

const Card = ({
    title,
    description,
    image,
}: {
    title: string;
    description: string;
    image: string;
}) => {
    return (
        <CardWrapper>
            <img
                style={{
                    height: 25,
                    width: 25,
                    marginRight: 16,
                    marginTop: 5,
                }}
                src={image}
                alt=""
            />
            <div>
                <HeadingText30>{title}</HeadingText30>
                <BodyTextMedium fontColor="gray65" marginTop={8}>
                    {description}
                </BodyTextMedium>
            </div>
        </CardWrapper>
    );
};

const QuoteWrapper = styled.div({
    width: '100%',
    marginBottom: 48,
    [mediaQuery.minWidth('md')]: {
        width: '45%',
    },
});

const Quote: React.FC<{ children: [comment: React.ReactNode, attribution: React.ReactNode] }> = ({
    children,
}) => {
    const [comment, attribution] = children;

    return (
        <QuoteWrapper>
            <CommentImage />
            <CommentText fontColor="gray65" marginTop={12}>
                {comment}
            </CommentText>
            <HeadingText20 marginTop={16}>{attribution}</HeadingText20>
        </QuoteWrapper>
    );
};
